import whatIfScenario from '../data/whatIfScenario.json';
// Use the available functions from the working damage service
import { isPointOnLand } from './damageValidationService';

// Hurricane track for Kingston Harbor landfall scenario
const KINGSTON_HURRICANE_TRACK = whatIfScenario.track.map(point => ({
  time: point.time,
  lat: point.lat,
  lon: point.lon,
  windSpeed: point.windSpeed,
  pressure: point.pressure,
  category: point.category,
  landfall: point.landfall || false
}));

// Critical infrastructure including Norman Manley Airport and Kingston Public Hospital
const CRITICAL_INFRASTRUCTURE = whatIfScenario.criticalInfrastructure;

// Simplified parish centers (fallback approach that works)
const KINGSTON_SCENARIO_PARISHES = {
  'Kingston': { centerLat: 17.997, centerLon: -76.794, pointCount: 450, severityWeight: 1.0 },
  'St. Andrew': { centerLat: 18.081, centerLon: -76.725, pointCount: 400, severityWeight: 0.95 },
  'St. Catherine': { centerLat: 17.973, centerLon: -77.055, pointCount: 350, severityWeight: 0.85 },
  'St. Thomas': { centerLat: 17.892, centerLon: -76.541, pointCount: 250, severityWeight: 0.7 },
  'Clarendon': { centerLat: 17.958, centerLon: -77.237, pointCount: 200, severityWeight: 0.6 },
  'Portland': { centerLat: 18.179, centerLon: -76.452, pointCount: 150, severityWeight: 0.5 },
  'St. Mary': { centerLat: 18.401, centerLon: -77.103, pointCount: 120, severityWeight: 0.4 },
  'Manchester': { centerLat: 18.046, centerLon: -77.507, pointCount: 100, severityWeight: 0.3 },
  'St. Ann': { centerLat: 18.435, centerLon: -77.203, pointCount: 80, severityWeight: 0.25 },
  'St. Elizabeth': { centerLat: 18.056, centerLon: -77.698, pointCount: 60, severityWeight: 0.2 },
  'Westmoreland': { centerLat: 18.224, centerLon: -78.131, pointCount: 40, severityWeight: 0.15 },
  'Hanover': { centerLat: 18.450, centerLon: -78.011, pointCount: 30, severityWeight: 0.1 },
  'St. James': { centerLat: 18.474, centerLon: -77.918, pointCount: 30, severityWeight: 0.1 },
  'Trelawny': { centerLat: 18.339, centerLon: -77.655, pointCount: 40, severityWeight: 0.15 }
};

// Generate a valid point within parish area using the working land validation
function generateValidPointInParish(parishConfig, maxAttempts = 50) {
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    // Generate point near parish center with reasonable spread
    const lat = parishConfig.centerLat + (Math.random() - 0.5) * 0.3; // ±0.15 degrees
    const lon = parishConfig.centerLon + (Math.random() - 0.5) * 0.3;
    
    // Use the existing land validation function
    if (isPointOnLand(lat, lon)) {
      return { lat, lon };
    }
    attempts++;
  }
  
  // Fallback to parish center if no valid point found
  return { 
    lat: parishConfig.centerLat, 
    lon: parishConfig.centerLon 
  };
}

// Calculate wind field based on Kingston landfall scenario
function calculateKingstonWindField(lat, lon) {
  const landfallPoint = KINGSTON_HURRICANE_TRACK.find(point => point.landfall);
  if (!landfallPoint) return { windSpeed: 0, category: 'none' };

  const distance = calculateDistance(lat, lon, landfallPoint.lat, landfallPoint.lon);
  const maxWindSpeed = landfallPoint.windSpeed; // 166 mph

  // Hurricane parameters for Category 5
  const eyeRadius = 8; // miles
  const eyewallRadius = 15; // miles
  const tropicalStormRadius = 150; // miles

  let windSpeed = 0;

  if (distance <= eyeRadius) {
    windSpeed = Math.random() * 20;
  } else if (distance <= eyewallRadius) {
    const eyewallPosition = (distance - eyeRadius) / (eyewallRadius - eyeRadius);
    windSpeed = maxWindSpeed * (1 - eyewallPosition * 0.1);
    
    // Norman Manley Airport gets full force
    const airportDistance = calculateDistance(lat, lon, 17.9357, -76.7875);
    if (airportDistance <= 2) {
      windSpeed = maxWindSpeed;
    }

    // Kingston Public Hospital gets near-maximum winds
    const hospitalDistance = calculateDistance(lat, lon, 17.9685, -76.7947);
    if (hospitalDistance <= 1.5) {
      windSpeed = Math.max(windSpeed, maxWindSpeed * 0.98); // 98% of max winds at KPH
    }
  } else if (distance <= tropicalStormRadius) {
    const outerPosition = (distance - eyewallRadius) / (tropicalStormRadius - eyewallRadius);
    windSpeed = maxWindSpeed * 0.9 * Math.exp(-outerPosition * 2);
  }

  // Check if location is in direct path of eye
  const distanceToPath = calculateDistanceToPath(lat, lon, KINGSTON_HURRICANE_TRACK);
  if (distanceToPath <= eyeRadius) {
    windSpeed = Math.max(windSpeed, maxWindSpeed * 1.15);
  }

  return {
    windSpeed: Math.round(windSpeed),
    category: windSpeedToDamageCategory(windSpeed),
    distanceFromLandfall: Math.round(distance),
    inEyePath: distanceToPath <= eyeRadius
  };
}

// Calculate flood damage for Kingston scenario
function calculateKingstonFloodDamage(lat, lon, elevation = null) {
  const landfallPoint = KINGSTON_HURRICANE_TRACK.find(point => point.landfall);
  if (!landfallPoint) return { floodLevel: 0, floodType: 'none' };

  const distanceFromCoast = calculateDistanceToCoast(lat, lon);
  const distanceFromLandfall = calculateDistance(lat, lon, landfallPoint.lat, landfallPoint.lon);
  
  // Special handling for Norman Manley Airport
  const airportDistance = calculateDistance(lat, lon, 17.9357, -76.7875);
  const isAirportArea = airportDistance <= 3;

  // Special handling for Kingston Public Hospital
  const hospitalDistance = calculateDistance(lat, lon, 17.9685, -76.7947);
  const isHospitalArea = hospitalDistance <= 2;

  // Kingston Harbor landfall creates massive storm surge
  let stormSurgeHeight = 0;
  if (distanceFromCoast <= 15) {
    if (isAirportArea) {
      stormSurgeHeight = 18;
    } else if (isHospitalArea) {
      stormSurgeHeight = 12; // KPH gets 12 feet of surge due to slightly higher elevation
    } else {
      stormSurgeHeight = Math.max(0, 22 - distanceFromCoast * 1.2);
    }
  }

  // Rainfall flooding
  let rainfallAmount = Math.max(0, 30 - distanceFromLandfall * 0.4);
  if (isAirportArea) {
    rainfallAmount = Math.max(rainfallAmount, 25);
  } else if (isHospitalArea) {
    rainfallAmount = Math.max(rainfallAmount, 28); // KPH area gets heavy rainfall
  }

  // Elevation factor
  const estimatedElevation = elevation || estimateElevation(lat, lon);
  let elevationFactor = Math.max(0.1, 1 - estimatedElevation / 100);
  
  if (isAirportArea) {
    elevationFactor = 0.95;
  } else if (isHospitalArea) {
    elevationFactor = 0.85; // KPH has slightly better elevation protection
  }

  // Combined flood level
  const combinedFlood = (stormSurgeHeight + rainfallAmount * 0.6) * elevationFactor;

  let floodLevel = 0;
  let floodType = 'none';

  if (combinedFlood >= 12) {
    floodLevel = 4;
    floodType = stormSurgeHeight > rainfallAmount ? 'storm surge' : 'combined';
  } else if (combinedFlood >= 8) {
    floodLevel = 4;
    floodType = stormSurgeHeight > rainfallAmount ? 'storm surge' : 'rainfall';
  } else if (combinedFlood >= 5) {
    floodLevel = 3;
    floodType = stormSurgeHeight > rainfallAmount ? 'storm surge' : 'rainfall';
  } else if (combinedFlood >= 2) {
    floodLevel = 2;
    floodType = stormSurgeHeight > rainfallAmount ? 'storm surge' : 'rainfall';
  } else if (combinedFlood >= 0.5) {
    floodLevel = 1;
    floodType = 'rainfall';
  }

  return {
    floodLevel,
    floodType,
    stormSurgeHeight: Math.round(stormSurgeHeight * 10) / 10,
    rainfallAmount: Math.round(rainfallAmount * 10) / 10,
    elevation: estimatedElevation,
    isAirportArea,
    isHospitalArea
  };
}

// Generate validated damage points using existing validation
export function generateKingstonDamagePoints() {
  const damagePoints = [];
  
  console.log('🎯 Starting Kingston scenario damage point generation...');

  // Add critical infrastructure points first
  Object.entries(CRITICAL_INFRASTRUCTURE).forEach(([name, info]) => {
    console.log(`🏥 Adding critical infrastructure: ${name}`);
    
    // Wind damage for infrastructure
    damagePoints.push({
      id: `infrastructure-wind-${name}`,
      lat: info.lat,
      lon: info.lon,
      parish: name.includes('airport') ? 'Kingston' : 'Kingston',
      damageType: 'wind',
      severity: 'catastrophic',
      windSpeed: info.windSpeed,
      distanceFromLandfall: 0,
      inEyePath: true,
      confidence: 1.0,
      source: 'Critical Infrastructure Analysis',
      infrastructureType: name,
      operationalStatus: info.status
    });

    // Flood damage for infrastructure
    if (info.stormSurge > 0) {
      damagePoints.push({
        id: `infrastructure-flood-${name}`,
        lat: info.lat + 0.0005,
        lon: info.lon + 0.0005,
        parish: name.includes('airport') ? 'Kingston' : 'Kingston',
        damageType: 'flood',
        severity: 'catastrophic',
        floodLevel: 4,
        floodType: 'storm surge',
        stormSurgeHeight: info.stormSurge,
        rainfallAmount: 25,
        elevation: info.elevation,
        confidence: 1.0,
        source: 'Critical Infrastructure Analysis',
        infrastructureType: name,
        operationalStatus: info.status
      });
    }
  });

  console.log(`✅ Added ${damagePoints.length} critical infrastructure points (including KPH)`);

  // Generate points for each parish
  Object.entries(KINGSTON_SCENARIO_PARISHES).forEach(([parishName, config]) => {
    console.log(`🏝️ Processing ${parishName}...`);
    
    const targetCount = config.pointCount;
    let windCount = 0;
    let floodCount = 0;
    let attempts = 0;
    const maxAttempts = targetCount * 5;

    while ((windCount + floodCount) < targetCount && attempts < maxAttempts) {
      attempts++;

      // Generate valid point in parish
      const point = generateValidPointInParish(config);
      if (!point) continue;

      const { lat, lon } = point;

      // Calculate Kingston scenario damage
      const windDamage = calculateKingstonWindField(lat, lon);
      const floodDamage = calculateKingstonFloodDamage(lat, lon);

      // Skip points with minimal impact
      if (windDamage.windSpeed < 30 && floodDamage.floodLevel < 1) continue;

      // Add wind damage point (60% of total)
      if (windDamage.category !== 'none' && windCount < targetCount * 0.6) {
        damagePoints.push({
          id: `kingston-wind-${parishName}-${windCount}`,
          lat: lat,
          lon: lon,
          parish: parishName,
          damageType: 'wind',
          severity: windDamage.category,
          windSpeed: windDamage.windSpeed,
          distanceFromLandfall: windDamage.distanceFromLandfall,
          inEyePath: windDamage.inEyePath,
          confidence: 0.85 + Math.random() * 0.15,
          source: 'Kingston Scenario Simulation'
        });
        windCount++;
      }

      // Add flood damage point (40% of total)
      if (floodDamage.floodLevel >= 1 && floodCount < targetCount * 0.4) {
        // Generate a slightly different point for flood damage
        const floodPoint = generateValidPointInParish(config);
        if (floodPoint) {
          damagePoints.push({
            id: `kingston-flood-${parishName}-${floodCount}`,
            lat: floodPoint.lat,
            lon: floodPoint.lon,
            parish: parishName,
            damageType: 'flood',
            severity: ['none', 'minor', 'moderate', 'major', 'catastrophic'][floodDamage.floodLevel],
            floodLevel: floodDamage.floodLevel,
            floodType: floodDamage.floodType,
            stormSurgeHeight: floodDamage.stormSurgeHeight,
            rainfallAmount: floodDamage.rainfallAmount,
            elevation: floodDamage.elevation,
            confidence: 0.8 + Math.random() * 0.2,
            source: 'Kingston Scenario Simulation',
            isAirportArea: floodDamage.isAirportArea || false,
            isHospitalArea: floodDamage.isHospitalArea || false
          });
          floodCount++;
        }
      }
    }
    
    console.log(`✅ ${parishName}: ${windCount} wind, ${floodCount} flood (${windCount + floodCount} total)`);
  });

  console.log(`🎯 KINGSTON SCENARIO TOTAL: ${damagePoints.length} damage points`);
  console.log(`🏥 Critical infrastructure included: Airport, Port, University Hospital, Kingston Public Hospital`);
  return damagePoints;
}

// Get hurricane track for Kingston scenario
export function getKingstonHurricaneTrack() {
  return KINGSTON_HURRICANE_TRACK;
}

// Get critical infrastructure data
export function getCriticalInfrastructure() {
  return CRITICAL_INFRASTRUCTURE;
}

// Helper functions
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

function calculateDistanceToPath(lat, lon, track) {
  let minDistance = Infinity;
  for (let i = 0; i < track.length - 1; i++) {
    const d = calculateDistance(lat, lon, track[i].lat, track[i].lon);
    minDistance = Math.min(minDistance, d);
  }
  return minDistance;
}

function calculateDistanceToCoast(lat, lon) {
  const coastalPoints = [
    { lat: 17.97, lon: -76.79 },     // Kingston Harbor
    { lat: 17.93, lon: -76.82 },     // Kingston coast
    { lat: 18.01, lon: -76.75 },     // Harbour View
    { lat: 17.88, lon: -76.85 },     // Portmore
    { lat: 17.9357, lon: -76.7875 },  // Norman Manley Airport
    { lat: 17.95, lon: -76.77 },     // Palisadoes
    { lat: 17.94, lon: -76.89 },     // Gregory Park
    { lat: 17.9685, lon: -76.7947 }  // Kingston Public Hospital area
  ];
  
  let minDistance = Infinity;
  coastalPoints.forEach(point => {
    const distance = calculateDistance(lat, lon, point.lat, point.lon);
    minDistance = Math.min(minDistance, distance);
  });
  
  return minDistance;
}

function windSpeedToDamageCategory(windSpeed) {
  if (windSpeed >= 157) return 'catastrophic';
  if (windSpeed >= 130) return 'destroyed';
  if (windSpeed >= 96) return 'major';
  if (windSpeed >= 74) return 'moderate';
  if (windSpeed >= 39) return 'minor';
  return 'none';
}

function estimateElevation(lat, lon) {
  const kingstonCenter = { lat: 17.97, lon: -76.79 };
  const airportLocation = { lat: 17.9357, lon: -76.7875 };
  const hospitalLocation = { lat: 17.9685, lon: -76.7947 };
  
  const distanceFromKingston = calculateDistance(lat, lon, kingstonCenter.lat, kingstonCenter.lon);
  const distanceFromAirport = calculateDistance(lat, lon, airportLocation.lat, airportLocation.lon);
  const distanceFromHospital = calculateDistance(lat, lon, hospitalLocation.lat, hospitalLocation.lon);
  
  // Airport area is 3 feet above sea level
  if (distanceFromAirport <= 2) {
    return 3 + Math.random() * 2;
  }
  
  // Kingston Public Hospital area is ~15 feet above sea level
  if (distanceFromHospital <= 1) {
    return 12 + Math.random() * 6; // 12-18 feet at hospital
  }
  
  // Kingston Harbor area is near sea level
  if (distanceFromKingston <= 3) {
    return Math.max(0, distanceFromKingston * 8 + Math.random() * 15);
  }
  
  // Elevations increase inland
  return Math.max(0, distanceFromKingston * 20 + Math.random() * 100);
}